<script lang="ts">
  import { site } from '$lib/config/site.config';
  import { apps } from '$apps/registry';

  $: toolCount = apps.length;
</script>

<section class="hero">
  <!-- Decorative gradient orbs -->
  <div class="hero-orb hero-orb-1" aria-hidden="true"></div>
  <div class="hero-orb hero-orb-2" aria-hidden="true"></div>
  <div class="hero-grid-pattern" aria-hidden="true"></div>

  <div class="hero-content">
    <div class="hero-badge">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
      Privacy-first · 100% local
    </div>

    <h1 class="hero-title">
      Every tool you need,<br/><span class="hero-highlight">in one place.</span>
    </h1>

    <p class="hero-subtitle">
      {toolCount}+ browser tools that work offline. Your files never leave your device — no uploads, no tracking, free forever.
    </p>

    <div class="hero-props">
      <div class="hero-prop">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        No uploads
      </div>
      <div class="hero-prop-divider" aria-hidden="true"></div>
      <div class="hero-prop">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        Works offline
      </div>
      <div class="hero-prop-divider" aria-hidden="true"></div>
      <div class="hero-prop">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
        Free forever
      </div>
    </div>
  </div>
</section>

<style>
  .hero {
    position: relative;
    padding: 3.5rem 1.5rem 3rem;
    text-align: center;
    overflow: hidden;
    background: hsl(var(--theme-hero-bg, var(--theme-background)));
  }

  /* Decorative gradient orbs */
  .hero-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.15;
    pointer-events: none;
    animation: float 20s ease-in-out infinite;
  }
  .hero-orb-1 {
    width: 400px;
    height: 400px;
    background: hsl(var(--theme-primary));
    top: -100px;
    left: -100px;
    animation-delay: -5s;
  }
  .hero-orb-2 {
    width: 350px;
    height: 350px;
    background: hsl(var(--theme-accent));
    bottom: -120px;
    right: -80px;
    animation-delay: -10s;
    opacity: 0.1;
  }

  /* Subtle grid pattern */
  .hero-grid-pattern {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(hsl(var(--theme-border) / 0.06) 1px, transparent 1px),
      linear-gradient(90deg, hsl(var(--theme-border) / 0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
    mask-image: radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse 80% 70% at 50% 50%, black 30%, transparent 70%);
  }

  .hero-content {
    position: relative;
    max-width: 680px;
    margin: 0 auto;
    z-index: 1;
  }

  /* Badge */
  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.3rem 0.875rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: hsl(var(--theme-primary));
    background: hsl(var(--theme-primary) / 0.08);
    border: 1px solid hsl(var(--theme-primary) / 0.15);
    margin-bottom: 1.25rem;
    animation: theme-fade-in 0.6s var(--theme-ease);
  }

  .hero-title {
    font-family: 'Inter', -apple-system, sans-serif;
    font-size: clamp(1.75rem, 4.5vw, 2.75rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.035em;
    color: hsl(var(--theme-foreground));
    margin-bottom: 0.875rem;
    animation: theme-slide-up 0.6s var(--theme-ease) 0.1s both;
  }

  .hero-highlight {
    background: linear-gradient(135deg, hsl(var(--theme-primary)), hsl(var(--theme-primary-hover)));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-subtitle {
    font-size: clamp(0.875rem, 1.5vw, 1.0625rem);
    line-height: 1.65;
    color: hsl(var(--theme-muted-foreground));
    max-width: 520px;
    margin: 0 auto 1.5rem;
    animation: theme-slide-up 0.6s var(--theme-ease) 0.2s both;
  }

  /* Value proposition pills */
  .hero-props {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    animation: theme-slide-up 0.6s var(--theme-ease) 0.3s both;
  }
  .hero-prop {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground));
  }
  .hero-prop svg {
    color: hsl(var(--theme-primary));
    flex-shrink: 0;
  }
  .hero-prop-divider {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: hsl(var(--theme-muted-foreground) / 0.3);
  }

  @media (max-width: 768px) {
    .hero { padding: 2.5rem 1rem 2rem; }
    .hero-orb-1 { width: 250px; height: 250px; }
    .hero-orb-2 { width: 200px; height: 200px; }
  }
  @media (max-width: 480px) {
    .hero { padding: 2rem 1rem 1.5rem; }
    .hero-props { gap: 0.5rem; }
    .hero-prop-divider { display: none; }
    .hero-prop {
      font-size: 0.75rem;
      padding: 0.25rem 0.625rem;
      background: hsl(var(--theme-muted) / 0.08);
      border-radius: 9999px;
      border: 1px solid hsl(var(--theme-border) / 0.5);
    }
  }
</style>
